import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import PageShell, { Breadcrumb } from "@/components/feature/PageShell";

export default function AboutInquiry() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get("phone_alt") as string;
    if (honeypot && honeypot.trim() !== "") {
      setStatus("success");
      form.reset();
      return;
    }
    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== "phone_alt" && typeof value === "string") {
        payload.append(key, value);
      }
    });
    try {
      const res = await fetch("https://readdy.ai/api/form/d972e6dtfb6ioa7s1u8g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || t("about.inquiry.error.submit");
        setErrorMsg(serverMsg);
        setStatus("error");
      }
    } catch {
      setErrorMsg(t("about.inquiry.error.network"));
      setStatus("error");
    }
  };

  return (
    <PageShell>
      <section className="relative pt-24 pb-20 bg-gradient-to-b from-primary-900 to-primary-800 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=Abstract%20flowing%20communication%20waves%20pattern%20with%20soft%20curves%20and%20dots%2C%20deep%20burgundy%20background%2C%20modern%20minimal%20design%2C%20elegant%20contact%20center%20aesthetic%2C%20warm%20subtle%20gradient%2C%20no%20text%2C%20no%20people&width=1600&height=600&seq=about-inquiry-hero-01&orientation=landscape')] bg-cover bg-center opacity-15"></div>
        <div className="relative w-full max-w-7xl mx-auto px-4 md:px-8">
          <Breadcrumb items={[{ label: t("nav.about") }, { label: t("about.inquiry.title") }]} />
          <div className="mt-6 max-w-3xl">
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-background-50 leading-tight">
              {t("about.inquiry.title")}
            </h1>
            <p className="mt-4 text-lg text-background-100/80 leading-relaxed">
              {t("about.inquiry.subtitle")}
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <div className="bg-background-50 rounded-lg p-8 border border-background-200">
                <h2 className="text-xl font-heading font-bold text-foreground-900 mb-6">{t("about.inquiry.form.title")}</h2>
                {status === "success" ? (
                  <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-6 text-center">
                    <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mb-3">
                      <i className="ri-check-line text-2xl"></i>
                    </div>
                    <h3 className="font-heading font-semibold text-foreground-900 text-lg mb-1">{t("about.inquiry.honeypot.success")}</h3>
                    <p className="text-sm text-foreground-600">{t("about.inquiry.honeypot.desc")}</p>
                    <button
                      onClick={() => setStatus("idle")}
                      className="mt-4 text-sm text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap"
                    >
                      {t("about.inquiry.honeypot.new")}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} data-readdy-form className="space-y-5">
                    {status === "error" && (
                      <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-4 py-3">{errorMsg}</div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="inquiry_name" className="block text-sm font-medium text-foreground-700 mb-1.5">{t("about.inquiry.name")} <span className="text-red-500">*</span></label>
                        <input id="inquiry_name" name="name" type="text" required className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors" placeholder={t("about.inquiry.placeholder.name")} />
                      </div>
                      <div>
                        <label htmlFor="inquiry_email" className="block text-sm font-medium text-foreground-700 mb-1.5">{t("about.inquiry.email")} <span className="text-red-500">*</span></label>
                        <input id="inquiry_email" name="email" type="email" required className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors" placeholder={t("about.inquiry.placeholder.email")} />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="inquiry_org" className="block text-sm font-medium text-foreground-700 mb-1.5">{t("about.inquiry.organization")}</label>
                      <input id="inquiry_org" name="organization" type="text" className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors" placeholder={t("about.inquiry.placeholder.org")} />
                    </div>
                    <div>
                      <label htmlFor="inquiry_type" className="block text-sm font-medium text-foreground-700 mb-1.5">{t("about.inquiry.type")} <span className="text-red-500">*</span></label>
                      <select id="inquiry_type" name="inquiry_type" required className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors">
                        <option value="">{t("about.inquiry.type.placeholder")}</option>
                        <option value="general">{t("about.inquiry.type.general")}</option>
                        <option value="cooperation">{t("about.inquiry.type.cooperation")}</option>
                        <option value="research">{t("about.inquiry.type.research")}</option>
                        <option value="evaluation">{t("about.inquiry.type.evaluation")}</option>
                        <option value="event">{t("about.inquiry.type.event")}</option>
                        <option value="other">{t("about.inquiry.type.other")}</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="inquiry_subject" className="block text-sm font-medium text-foreground-700 mb-1.5">{t("about.inquiry.subject")} <span className="text-red-500">*</span></label>
                      <input id="inquiry_subject" name="subject" type="text" required className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors" placeholder={t("about.inquiry.placeholder.subject")} />
                    </div>
                    <div>
                      <label htmlFor="inquiry_message" className="block text-sm font-medium text-foreground-700 mb-1.5">{t("about.inquiry.message")} <span className="text-red-500">*</span></label>
                      <textarea
                        id="inquiry_message"
                        name="message"
                        required
                        maxLength={500}
                        rows={5}
                        className="w-full px-4 py-2.5 text-sm border border-background-300 rounded-md bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder={t("about.inquiry.placeholder.message")}
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1">{t("about.inquiry.chars")}</p>
                    </div>
                    <div className="hp-field-alt">
                      <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>
                    <button type="submit" className="bg-primary-600 text-background-50 px-8 py-3 rounded-md text-sm font-medium hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap">
                      {t("about.inquiry.submit")}
                    </button>
                  </form>
                )}
              </div>
            </div>

            <aside className="space-y-6">
              <div className="bg-primary-50 rounded-lg p-6 border border-primary-200">
                <h3 className="font-heading font-semibold text-primary-800 mb-4">{t("about.inquiry.contact")}</h3>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t("about.inquiry.address.label")}</p>
                      <p className="text-sm text-foreground-600">{t("about.inquiry.address")}</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t("about.inquiry.email.label")}</p>
                      <p className="text-sm text-foreground-600">info@iabs.com.hk</p>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-phone-line"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t("about.inquiry.phone.label")}</p>
                      <p className="text-sm text-foreground-600">+852 2856 0333</p>
                    </div>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </PageShell>
  );
}